import { Vector } from './Vector'
import { Plane } from './Plane'
import { CarGameObject, SignGameObject, StationaryGameObject } from './GameObject'
import { PlaneRenderable, SpriteRenderable, CenteredSpriteWithHalfExtents, GridRenderable, TriangularBarrierRenderable } from './Renderable'
import { Collision } from './Collision'
import { TimeStep, Frequency } from './Timing'
import { InputState } from './InputState'
import { Grid, GridObject } from './Grid'
import { SparkParticle } from './SparkParticle'
import * as PIXI from 'pixi.js'

/*0-500            2000
*-----
*0  0.25            1               2               3
*-----
*      0-500
*      -----
* 
*      -----
*      
* x*2000
* 
*/


let app: PIXI.Application;
let worldContainer: PIXI.Container;
let hudContainer: PIXI.Container;
let hudText: PIXI.Text;
let hudLimitSign: PIXI.Sprite | null = null;
let hudSpeedingWarning: PIXI.Text | null = null;
let debugGraphics: PIXI.Graphics;
let effectsGraphics: PIXI.Graphics;

// Touch controls
let touchHints: {
    container: PIXI.Container | null;
    opacity: number;
    fadeStartTime: number;
    hasInteracted: boolean;
} = {
    container: null,
    opacity: 0.6,
    fadeStartTime: 0,
    hasInteracted: false
};

const TOUCH_HINT_FADE_DELAY_MS = 3000;  // Show hints for 3 seconds
const TOUCH_HINT_FADE_DURATION_MS = 1000;  // Then fade over 1 second

// Debug mode controls
let debugMode = {
    steering: false,
    showCollisionGeometry: false,
    disableInterpolation: false,
    text: ''
};

class GameState {
    timeStep: TimeStep; //(FIXED_TIMESTEP_MS),
    fpsCounter: Frequency;
    logicCounter: Frequency;
    warnings: number;
    spatialGridOrigin: Vector;
    car: CarGameObject | null;
    walls: StationaryGameObject[];
    //topPlane: Plane | null;
    //bottomPlane: Plane | null;
    startTime: Date;
    signs: SignGameObject[];
    signBoundaries: SignGameObject[];
    signBoundaryGridIds: string[];
    signBoundaryLimitById: Map<string, number>;
    track: StationaryGameObject[];
    topWalls: StationaryGameObject[];
    bottomWalls: StationaryGameObject[];
    currentLimit: number;
    currentSpeed: number;
    distanceTravelledMiles: number;
    reported: boolean;
    spatialGrid: Grid<DynamicsGridObject> | null;
    elapsedMs: number;
    lastSparkSpawnMs: number;
    sparks: SparkParticle[];
    // private app: PIXI.Application;
    // private container: PIXI.Container;
    // private titleText!: PIXI.Text;  // Initialized in onEnter
    // private subtitleText!: PIXI.Text;  // Initialized in onEnter
    // private stateManager: GameStateManager;
    constructor() { //app: PIXI.Application, stateManager: GameStateManager) {
        this.timeStep = new TimeStep(FIXED_TIMESTEP_MS);
        this.fpsCounter = new Frequency();
        this.logicCounter = new Frequency();
        this.warnings = 0;
        this.spatialGridOrigin = new Vector(0, 0, 0);
        this.car = null;
        this.walls = [];
        //this.topPlane = null;
        //this.bottomPlane = null;
        this.startTime = new Date();
        this.signs = [];
        this.signBoundaries = [];
        this.signBoundaryGridIds = [];
        this.signBoundaryLimitById = new Map<string, number>();
        this.track = [];
        this.topWalls = [];
        this.bottomWalls = [];
        this.currentLimit = 30;
        this.currentSpeed = 0;
        this.distanceTravelledMiles = 0;
        this.reported = false;
        this.spatialGrid = null;
        this.elapsedMs = 0;
        this.lastSparkSpawnMs = -50;
        this.sparks = [];
    }


    run() {
        this.fpsCounter.calcFPS();

        // Run physics updates at fixed 30fps
        while (this.timeStep.isPending()) {
            this.logicCounter.calcFPS();
            this.update();
            this.timeStep.inc();
        }

        // Render as fast as possible (monitor refresh rate)
        // Toggle interpolation for debugging (press I to see the difference)
        this.draw(debugMode.disableInterpolation ? 0 : this.timeStep.getStepFraction());

    }

    // per frame game logic update
    update() {
        if (!this.car || !this.spatialGrid)
            return;

        this.elapsedMs += FIXED_TIMESTEP_MS;

        for (const spark of this.sparks) {
            spark.update(FIXED_TIMESTEP_MS);
        }

        // Filter out dead sparks
        this.sparks = this.sparks.filter(spark => !spark.isDead());

        this.spatialGrid.clearGrid();

        const drag = Vector.scale(unitForce, 4 * (Math.pow(this.currentSpeed, 2) * (1 / Math.pow(170, 2))));
        const unitForceMagnitude = Vector.magnitude(unitForce);
        const dragMagnitude = Vector.magnitude(drag);
        const brakingForceMagnitude = BRAKE_FORCE_SCALE * unitForceMagnitude + BRAKE_DRAG_SCALE * dragMagnitude;

        const force = inputState.shouldRespond(KEY_LEFT)
            ? 0 - brakingForceMagnitude
            : unitForceMagnitude * 6 - dragMagnitude;

        const turnRightRequested = inputState.shouldRespond(KEY_DOWN);
        const turnLeftRequested = inputState.shouldRespond(KEY_UP);
        this.car.Steering.applyInput(turnRightRequested, turnLeftRequested);

        this.car.Move(force, this.car.Steering.currentTurnMatrix());

        // Position is represented in miles in world-space, so movement magnitude is miles travelled.
        this.distanceTravelledMiles += Vector.magnitude(this.car.Movement);

        for (let wallIndex = 0; wallIndex < this.topWalls.length; wallIndex++) {
            const boundaryWall = this.topWalls[wallIndex];
            this.spatialGrid.addStatic({
                Id: `wall-${wallIndex}`,
                Type: 'wall',
                Position: boundaryWall.position,
                halfExtents: boundaryWall.halfExtents,
                collisionPlanes: boundaryWall.collisionPlanes
            });
        }

        for (let wallIndex = 0; wallIndex < this.bottomWalls.length; wallIndex++) {
            const boundaryWall = this.bottomWalls[wallIndex];
            this.spatialGrid.addStatic({
                Id: `wall-${wallIndex}`,
                Type: 'wall',
                Position: boundaryWall.position,
                halfExtents: boundaryWall.halfExtents,
                collisionPlanes: boundaryWall.collisionPlanes
            });
        }

        for (let wallIndex = 0; wallIndex < this.signBoundaries.length; wallIndex++) {
            const boundaryWall = this.signBoundaries[wallIndex];
            const boundaryId = this.signBoundaryGridIds[wallIndex];
            this.spatialGrid.addStatic({
                Id: boundaryId,
                Type: 'signBoundary',
                Position: boundaryWall.position,
                halfExtents: boundaryWall.radius
            });
        }

        for (let wallIndex = 0; wallIndex < this.walls.length; wallIndex++) {
            const wall = this.walls[wallIndex];
            const boundaryId = `wall-${wallIndex}`;
            this.spatialGrid.addStatic({
                Id: boundaryId,
                Type: 'wall',
                Position: wall.position,
                halfExtents: wall.halfExtents,
                collisionPlanes: wall.collisionPlanes
            });
        }

        const carGridCandidates = this.spatialGrid.addDynamic({
            Id: 'car-player',
            Type: 'car',
            Position: this.car.ToPosition,
            halfExtents: this.car.halfExtents
        });

        const collisionNudge = oneFoot;
        if (carGridCandidates.length > 0) {
            debugMode.text = '';
            for (const collider of carGridCandidates) {
                const cellsText = collider.cells?.map(c => `(${c.column},${c.row})`).join(' ');
                debugMode.text += `Collision candidate: car-player/car with ${collider.Id}/${collider.Type} cells: ${cellsText}\n`;

                // if (collider.Type === 'wall') {
                //     // Use pre-computed collision planes from the grid object
                //     if (!collider.collisionPlanes) continue;

                //     // Test against all collision planes for this wall
                //     for (const plane of collider.collisionPlanes) {
                //         if (Collision.PlaneIntersection(this.car, plane)) {
                //             const velocityBeforeResponse = this.car.ToVelocity;
                //             this.car.ToVelocity = Vector.removeNormalComponentIfToward(this.car.ToVelocity, plane.normal);

                //             // Reset steering for vertical collisions (top/bottom faces)
                //             if (Math.abs(plane.normal.y) > 0.5) {
                //                 this.car.Steering.reset();
                //             }

                //             this.car.ToPosition = Vector.add(this.car.ToPosition, Vector.scale(plane.normal, collisionNudge));
                //             this.car.Movement = Vector.subtract(this.car.ToPosition, this.car.FromPosition);
                //             spawnCollisionSparks(this, this.car.ToPosition, this.car.halfExtents, plane.normal, velocityBeforeResponse);
                //         }
                //     }
                // }

                if (collider.Type === 'wall') { //} || collider.Type === 'barrier') {
                    // Use pre-computed collision planes from the grid object
                    if (!collider.collisionPlanes) continue;

                    // Test against all collision planes for this wall
                    const furtherTests: Plane[] = [];
                    let hadCollision = false;
                    for (const plane of collider.collisionPlanes) {
                        if (Collision.PlaneIntersection(this.car, plane)) {
                            const velocityBeforeResponse = this.car.ToVelocity;
                            this.car.ToVelocity = Vector.removeNormalComponentIfToward(this.car.ToVelocity, plane.normal);

                            // Reset steering for vertical collisions (top/bottom faces)
                            if (Math.abs(plane.normal.y) > 0.5) {
                                this.car.Steering.reset();
                            }

                            this.car.ToPosition = Vector.add(this.car.ToPosition, Vector.scale(plane.normal, collisionNudge));
                            this.car.Movement = Vector.subtract(this.car.ToPosition, this.car.FromPosition);
                            spawnCollisionSparks(this, this.car.ToPosition, this.car.halfExtents, plane.normal, velocityBeforeResponse);
                            hadCollision = true;
                        } else {
                            furtherTests.push(plane);
                            // For barriers, also check if we're already penetrated and need constraint
                            //if (Collision.PlaneConstraint(this.car, plane)) {
                            //    const velocityBeforeResponse = this.car.ToVelocity;
                            //    this.car.ToVelocity = Vector.removeNormalComponentIfToward(this.car.ToVelocity, plane.normal);
                            //    spawnCollisionSparks(this, this.car.ToPosition, this.car.halfExtents, plane.normal, velocityBeforeResponse);
                            //    break; // If we hit a barrier, don't check other planes to avoid multiple spark spawns
                            //   
                            //}
                        }
                    }

                    if (!hadCollision && furtherTests.length > 0) {
                        // Sort planes by distance (closest first) to handle the most relevant penetration
                        const planesWithDistance = furtherTests.map(plane => ({
                            plane,
                            distance: Math.abs(Plane.halfPlaneTest(plane, this.car!.FromPosition) - this.car!.radius(plane))
                        }));
                        planesWithDistance.sort((a, b) => a.distance - b.distance);
                        
                        // Check closest plane first
                        const closestPlane = planesWithDistance[0].plane;
                        if (Collision.PlaneConstraint(this.car, closestPlane)) {
                            const velocityBeforeResponse = this.car.ToVelocity;
                            this.car.ToVelocity = Vector.removeNormalComponentIfToward(this.car.ToVelocity, closestPlane.normal);
                            spawnCollisionSparks(this, this.car.ToPosition, this.car.halfExtents, closestPlane.normal, velocityBeforeResponse);
                        }
                    }
                }

                if (collider.Type === 'signBoundary') {
                    const signLimit = this.signBoundaryLimitById.get(collider.Id);
                    if (signLimit !== undefined) {
                        this.currentLimit = signLimit;
                    }
                }
            }
        }

        this.currentSpeed = Math.round(Vector.magnitude(this.car.ToVelocity) * 1000 * 60 * 60);
    }

    draw(interpolationValue: number) {
        if (!this.car || !this.spatialGrid) return;  // Guard against null car/wall/grid
        const car = this.car;
        // const wall = state.wall;
        // const spatialGrid = state.spatialGrid;
        debugGraphics.clear();
        effectsGraphics.clear();

        for (const spark of this.sparks) {
            spark.draw(effectsGraphics);
        }

        const gridDebug = new GridRenderable(debugGraphics, this.spatialGrid);
        gridDebug.update(debugMode.showCollisionGeometry);

        // Camera system: car should appear at camera position on screen
        const worldScale = 4000 * 5.5;

        for (const trackSection of this.track) {
            trackSection.updateSprite(debugMode.showCollisionGeometry);
        }

        for (const wall of this.topWalls) {
            wall.updateSprite(debugMode.showCollisionGeometry);
        }

        for (const wall of this.bottomWalls) {
            wall.updateSprite(debugMode.showCollisionGeometry);
        }

        for (const sign of this.signs) {
            sign.updateSprite(debugMode.showCollisionGeometry);
        }

        for (const sign of this.signBoundaries) {
            sign.updateSprite(debugMode.showCollisionGeometry);
        }

        const carRenderPosition = car.updateSprite(interpolationValue, debugMode.showCollisionGeometry);

        for (const wall of this.walls) {
            wall.updateSprite(debugMode.showCollisionGeometry);
        }

        // where we'll anchor the camera
        const screenCameraLocation = new Vector(app.screen.width / 10, app.screen.height / 2, 0);
        //let camera = new Vector(0.01, 0.02, 0);
        // Pivot on the car's interpolated sprite position (smooth camera follow)
        // camera is following the car
        const camera = carRenderPosition;

        const translation = Vector.add(
            screenCameraLocation,
            // multiple to same scale screen coords
            Vector.scale(
                // translation for camera to origin
                Vector.subtract(new Vector(0, 0, 0), camera),
                worldScale)
        );
        // worldContainer.pivot.set(carRenderPosition.x, carRenderPosition.y);
        // Position where the pivot (car) appears on screen = camera position scaled

        worldContainer.scale.set(worldScale);
        worldContainer.position.set(translation.x, translation.y); //camera.y * worldScale);

        // Update HUD (screen-space)
        const debugInfo = (debugMode.showCollisionGeometry ? " [D:ON]" : "[D:OFF]")
            + (debugMode.disableInterpolation ? " [I:OFF]" : "[I:OFF]")
            + (debugMode.steering ? " [A:ON]" : "[A:OFF]")
            + '\n' + ` ${debugMode.text}`;

        const hudMessage = "MPH: " + this.currentSpeed
            + "  Time: " + Math.floor((new Date().getTime() - this.startTime.getTime()) / (1000))
            + "  Limit: " + this.currentLimit
            + "  Distance: " + this.distanceTravelledMiles.toFixed(3) + " mi"
            + "  FPS: " + Math.round(this.fpsCounter.getResult())
            + "  Logic FPS: " + Math.round(this.logicCounter.getResult())
            + debugInfo;
        hudText.text = hudMessage;

        // Update speed limit sign
        if (hudLimitSign) {
            let signPath = '';
            switch (this.currentLimit) {
                case 30:
                    signPath = '/images/signs/30.png';
                    break;
                case 50:
                    signPath = '/images/signs/50.png';
                    break;
                case 70:
                    signPath = '/images/signs/limit.png';
                    break;
                case 0:
                    signPath = '/images/signs/stop.png';
                    break;
                default:
                    signPath = '/images/signs/30.png';
            }
            const texture = PIXI.Assets.get(signPath);
            if (texture && hudLimitSign.texture !== texture) {
                hudLimitSign.texture = texture;
            }

            // Update scale and position (responsive to window size)
            const scale = getSignScale();
            hudLimitSign.scale.set(scale);
            const pos = getSignPosition();
            hudLimitSign.position.set(pos.x, pos.y);
        }

        // Update speeding warning (blinking !)
        if (hudSpeedingWarning) {
            const isSpeeding = this.currentLimit > 0 && this.currentSpeed > this.currentLimit;
            if (isSpeeding) {
                // Blink every 250ms
                const blinkCycle = Math.floor(this.elapsedMs / 250) % 2;
                hudSpeedingWarning.visible = blinkCycle === 0;
            } else {
                hudSpeedingWarning.visible = false;
            }
            // Update position and font size to match sign scale
            const warningPos = getWarningPosition();
            hudSpeedingWarning.position.set(warningPos.x, warningPos.y);
            hudSpeedingWarning.style.fontSize = getWarningFontSize();
            hudSpeedingWarning.style.stroke = { color: 0xffff00, width: getSignScale() * 30 }; // Scale stroke width too
        }

        // Update touch hints opacity
        if (touchHints.container && touchHints.opacity > 0) {
            const elapsed = Date.now() - touchHints.fadeStartTime;
            if (elapsed > TOUCH_HINT_FADE_DELAY_MS) {
                const fadeProgress = (elapsed - TOUCH_HINT_FADE_DELAY_MS) / TOUCH_HINT_FADE_DURATION_MS;
                touchHints.opacity = Math.max(0, 0.6 * (1 - fadeProgress));
                touchHints.container.alpha = touchHints.opacity;
            }
        }
    }


    async init(): Promise<void> {

        const trackWidth = 0.006;
        const totalTrackLength = 3;
        const trackCentreY = 0.00;
        const wallWidth = trackWidth / 3;
        for (var index = 0; index < (totalTrackLength / trackWidth); index++) {
            const trackx = 0.00 + (trackWidth * index);
            var trackSection = new StationaryGameObject(
                new SpriteRenderable(
                    new CenteredSpriteWithHalfExtents(worldContainer, "/images/signs/track.png", trackWidth, null),
                    debugGraphics, true),
                new Vector(trackx, trackCentreY, 0));

            const wallx = trackx - (trackWidth / 2) + (wallWidth / 2);;

            for (var windex = 0; windex < 3; windex++) {
                const wallPos = new Vector(wallx + (wallWidth * windex), trackCentreY - trackSection.halfExtents.y - (wallWidth / 2), 0);
                const wallHalfExtents = new Vector(wallWidth / 2, wallWidth / 2, 0);

                // Pre-compute collision plane for top wall (normal pointing down into track)
                const topPlane = new Plane(new Vector(0, 1, 0), -wallPos.y - wallHalfExtents.y);
                const collisionPlanes = [topPlane];
                const planeRenderables = collisionPlanes.map(plane => new PlaneRenderable(debugGraphics, plane));

                var wallBoudary = new StationaryGameObject(
                    new SpriteRenderable(
                        new CenteredSpriteWithHalfExtents(worldContainer, "/images/signs/wall.png", wallWidth, null),
                        debugGraphics, true),
                    wallPos,
                    collisionPlanes,
                    planeRenderables);
                this.topWalls.push(wallBoudary);
            }

            for (var windex = 0; windex < 3; windex++) {
                const wallPos = new Vector(wallx + (wallWidth * windex), trackCentreY + trackSection.halfExtents.y + (wallWidth / 2), 0);
                const wallHalfExtents = new Vector(wallWidth / 2, wallWidth / 2, 0);

                // Pre-compute collision plane for bottom wall (normal pointing up into track)
                const bottomPlane = new Plane(new Vector(0, -1, 0), wallPos.y - wallHalfExtents.y);
                const collisionPlanes = [bottomPlane];
                const planeRenderables = collisionPlanes.map(plane => new PlaneRenderable(debugGraphics, plane));

                var wallBoudary = new StationaryGameObject(
                    new SpriteRenderable(
                        new CenteredSpriteWithHalfExtents(worldContainer, "/images/signs/wall.png", wallWidth, null),
                        debugGraphics, true),
                    wallPos,
                    collisionPlanes,
                    planeRenderables);
                this.bottomWalls.push(wallBoudary);
            }
            this.track.push(trackSection);
        }

        const signDefinitions: Array<{ x: number; image: string; limit: number }> = [
            { x: 0.01, image: "/images/signs/30.png", limit: 30 },
            { x: 0.1 * 2, image: "/images/signs/50.png", limit: 50 },
            { x: 0.16 * 2, image: "/images/signs/limit.png", limit: 70 },
            { x: 0.25 * 2, image: "/images/signs/50.png", limit: 50 },
            { x: 0.32 * 2, image: "/images/signs/30.png", limit: 30 },
            { x: 0.4 * 2, image: "/images/signs/50.png", limit: 50 },
            { x: 0.5 * 2, image: "/images/signs/30.png", limit: 30 },
            { x: 0.6 * 2, image: "/images/signs/50.png", limit: 50 },
            { x: 0.7 * 2, image: "/images/signs/limit.png", limit: 70 },
            { x: 0.85 * 2, image: "/images/signs/50.png", limit: 50 },
            { x: 0.9 * 2, image: "/images/signs/30.png", limit: 30 }
        ];

        const signSize = oneFoot * 5; // / 5280;
        for (let signIndex = 0; signIndex < signDefinitions.length; signIndex++) {
            const signDefinition = signDefinitions[signIndex];
            const currentSign = new SignGameObject(
                new SpriteRenderable(
                    new CenteredSpriteWithHalfExtents(worldContainer, signDefinition.image, signSize, null),
                    debugGraphics,
                    false),
                new Vector(signDefinition.x, trackCentreY - (trackWidth / 2) - signSize, 0), // -0.0001, 0),
                signDefinition.limit
            );

            this.signs.push(currentSign);

            const height = CAR_WIDTH_WORLD_UNITS; // 0.03 / 2.5;
            for (var windex = 0; windex < 4; windex++) {
                const signBoundaryId = `signBoundary-${signIndex}-${windex}`;
                var signBoudary = new SignGameObject(
                    new SpriteRenderable(
                        new Vector(signSize / 2, height / 2, 0),
                        debugGraphics, true),
                    new Vector(signDefinition.x, trackCentreY - trackWidth + (height * windex), 0),
                    signDefinition.limit
                );
                this.signBoundaries.push(signBoudary);
                this.signBoundaryGridIds.push(signBoundaryId);
                this.signBoundaryLimitById.set(signBoundaryId, signDefinition.limit);
            }
        }

        type WallFace = 'left' | 'top' | 'bottom';
        type WallRunFace = 'top' | 'bottom';
        type WallDefinition = {
            x: number;
            y: number;
            faces: Set<WallFace>; //[];
            type: 'centralBarrier' | 'topBarrier' | 'bottomBarrier' | 'wall';
        };


        const toType = (faces: Set<WallRunFace>): 'centralBarrier' | 'topBarrier' | 'bottomBarrier'  => {
            if (['top', 'bottom'].every(face => faces.has(face as WallRunFace))) {
                return 'centralBarrier';
            } else if (faces.has('top')) {
                return 'topBarrier';
            } else if (faces.has('bottom')) {
                return 'bottomBarrier';
            }
            throw new Error('Invalid wall run face configuration');
        };

        // Helper function to create a run of adjacent walls
        // First wall in run is always a left-pointing triangular barrier
        const createWallRun = (startX: number, y: number, count: number, faces: Set<WallRunFace>): WallDefinition[] => {

            const walls: WallDefinition[] = [];
            for (let i = 0; i < count; i++) {
                walls.push({
                    x: startX + wallWidth * i,
                    y: y,
                    faces: i === 0 ? new Set(['left', ...faces]) : faces,
                    type: i === 0 ? toType(faces) : 'wall'
                });
            }
            return walls;
        };

        const wallDefinitions: WallDefinition[] = [
            // {x: 0.09 * 2, y: trackCentreY - (trackWidth / 3), faces: new Set(['left', 'bottom']), type: 'wall' },
            ...createWallRun(0.2, trackCentreY - (trackWidth / 3), 6, new Set(['bottom'])),
            ...createWallRun(0.32, trackCentreY + (trackWidth / 3), 6, new Set(['top'])),
            ...createWallRun(0.36, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(0.4, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(0.5, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(0.64, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(0.68, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(0.72, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(0.76, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(0.8, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(1.0, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(1.1, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(1.2, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(1.3, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(1.36, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(1.4, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(1.46, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(1.5, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),

            ...createWallRun(1.6, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(1.6, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),

            ...createWallRun(1.64, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(1.7, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(1.72, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(1.76, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(1.9, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),

            /*
            ...createWallRun(0.82 * 2, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(0.85 * 2, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(0.86 * 2, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(0.88 * 2, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(0.95 * 2, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
*/
            ...createWallRun(2.14, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(2.2, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(2.22, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(2.26, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(2.4, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),

            ...createWallRun(2.64, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(2.7, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(2.72, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(2.76, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
            ...createWallRun(2.9, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),


            ...createWallRun(3, trackCentreY - (trackWidth / 3), 3, new Set(['bottom'])),
            ...createWallRun(3, trackCentreY, 3, new Set(['top', 'bottom'])),
            ...createWallRun(3, trackCentreY + (trackWidth / 3), 3, new Set(['top'])),
        ];

        for (let signIndex = 0; signIndex < wallDefinitions.length; signIndex++) {
            const wallDefinition = wallDefinitions[signIndex];
            const wallPos = new Vector(wallDefinition.x, wallDefinition.y, 0);
            //const wallHalfExtents = new Vector(wallWidth / 2, wallWidth / 2, 0);
            const collisionPlanes: Plane[] = [];

            // the standard wall sprite to use.
            const wallSprite = new CenteredSpriteWithHalfExtents(worldContainer, "/images/signs/wall.png", wallWidth, null);

            if (wallDefinition.type === 'centralBarrier') {
                // Create angled collision planes for left-pointing triangular barrier (<)
                // Two planes form the V shape pointing left
                const triHalfWidth = wallSprite.radius.x; // wallHalfExtents.x;
                const triHalfHeight = wallSprite.radius.y; // wallHalfExtents.y;

                // Collision geometry matches visual size
                //const triHalfWidth = halfWidth;
                //const triHalfHeight = halfHeight;

                // Left-pointing triangle < with apex at left, base at right
                // apex at (-triHalfWidth, 0), top-right at (triHalfWidth, -triHalfHeight), bottom-right at (triHalfWidth, triHalfHeight)
                // Top edge: from apex to top-right
                // Edge vector: (2*triHalfWidth, -triHalfHeight)
                // Outward normal (rotate edge 90° CW): (-triHalfHeight, -2*triHalfWidth) - points left and down
                const topEdgeNormal = Vector.normalise(new Vector(-triHalfHeight, -2 * triHalfWidth, 0));
                const apexPoint = Vector.add(wallPos, new Vector(-triHalfWidth, 0, 0));
                // Plane equation: n·p + d = 0, so d = -n·p
                const topPlane = new Plane(topEdgeNormal, -Vector.dotProduct(topEdgeNormal, apexPoint));

                // Bottom edge: from apex to bottom-right
                // Edge vector: (2*triHalfWidth, triHalfHeight)
                // Outward normal (rotate edge 90° CCW): (-triHalfHeight, 2*triHalfWidth) - points left and up
                const bottomEdgeNormal = Vector.normalise(new Vector(-triHalfHeight, 2 * triHalfWidth, 0));
                const bottomPlane = new Plane(bottomEdgeNormal, -Vector.dotProduct(bottomEdgeNormal, apexPoint));

                // No base plane needed - apex point collision detection handles head-on approaches

                collisionPlanes.push(topPlane, bottomPlane);
                const planeRenderables = collisionPlanes.map(plane => new PlaneRenderable(debugGraphics, plane));

                const barrier = new StationaryGameObject(
                    new TriangularBarrierRenderable(
                        wallSprite, //worldContainer,
                        debugGraphics,
                        'left',  // orientation for < shape
                        true,  // canCollide
                        0x808080  // fillColor
                    ),
                    wallPos,
                    collisionPlanes,
                    planeRenderables
                );

                this.walls.push(barrier);
            } else if (wallDefinition.type === 'bottomBarrier') {
                // Create single slanted plane for top barrier (\)
                // Slanted wall from top-left to bottom-right
                // Surface direction: (1, 1, 0) normalized
                // Outward normal (rotate 90° CCW): (-1, 1, 0) normalized - points up and left
                const slantNormal = Vector.normalise(new Vector(-1, 1, 0));
                const slantPlane = new Plane(slantNormal, -Vector.dotProduct(slantNormal, wallPos));

                collisionPlanes.push(slantPlane);
                const planeRenderables = collisionPlanes.map(plane => new PlaneRenderable(debugGraphics, plane));

                const barrier = new StationaryGameObject(
                    new TriangularBarrierRenderable(
                        wallSprite,
                        debugGraphics,
                        'top',  // orientation for \ shape
                        true,  // canCollide
                        0x808080  // fillColor
                    ),
                    wallPos,
                    collisionPlanes,
                    planeRenderables
                );

                this.walls.push(barrier);
            } else if (wallDefinition.type === 'topBarrier') {
                // Create single slanted plane for bottom barrier (/)
                // Slanted wall from bottom-left to top-right
                // Surface direction: (1, -1, 0) normalized
                // Outward normal (rotate 90° CCW): (1, 1, 0) normalized - points up and right
                // But we want it to deflect cars back into track, so flip: (-1, -1, 0) - points down and left
                const slantNormal = Vector.normalise(new Vector(-1, -1, 0));
                const slantPlane = new Plane(slantNormal, -Vector.dotProduct(slantNormal, wallPos));

                collisionPlanes.push(slantPlane);
                const planeRenderables = collisionPlanes.map(plane => new PlaneRenderable(debugGraphics, plane));

                const barrier = new StationaryGameObject(
                    new TriangularBarrierRenderable(
                        wallSprite,
                        debugGraphics,
                        'bottom',  // orientation for / shape
                        true,  // canCollide
                        0x808080  // fillColor
                    ),
                    wallPos,
                    collisionPlanes,
                    planeRenderables
                );

                this.walls.push(barrier);
            } else {
                // Regular wall: Pre-compute collision planes based on specified faces
                for (const face of wallDefinition.faces) {
                    switch (face) {
                        case 'left':
                            collisionPlanes.push(new Plane(new Vector(-1, 0, 0), wallPos.x - wallSprite.radius.x));
                            break;
                        case 'top':
                            collisionPlanes.push(new Plane(new Vector(0, -1, 0), wallPos.y - wallSprite.radius.y));
                            break;
                        case 'bottom':
                            collisionPlanes.push(new Plane(new Vector(0, 1, 0), -wallPos.y - wallSprite.radius.y));
                            break;
                    }
                }

                const planeRenderables = collisionPlanes.map(plane => new PlaneRenderable(debugGraphics, plane));

                const wall = new StationaryGameObject(
                    new SpriteRenderable(
                        // new CenteredSpriteWithHalfExtents(worldContainer, "/images/signs/wall.png", wallWidth, null),
                        wallSprite,
                        debugGraphics,
                        true),
                    wallPos,
                    collisionPlanes,
                    planeRenderables
                );
                this.walls.push(wall);
            }
        }

        this.car = new CarGameObject(
            new SpriteRenderable(
                new CenteredSpriteWithHalfExtents(worldContainer, "/images/signs/car.png", CAR_WIDTH_WORLD_UNITS, null), // 0.00257, null),
                debugGraphics,
                true),
            new Vector(0.00, trackCentreY, 0)
        );

        if (!this.car || !this.walls) {
            throw new Error('Game state initialization failed');
        }

        // this.car.Crashed = false;

        const minTrackX = this.track.reduce((min, section) => Math.min(min, section.position.x - section.halfExtents.x), Number.POSITIVE_INFINITY);
        const maxTrackX = this.track.reduce((max, section) => Math.max(max, section.position.x + section.halfExtents.x), Number.NEGATIVE_INFINITY);
        const minTrackY = this.track.reduce((min, section) => Math.min(min, section.position.y - section.halfExtents.y), Number.POSITIVE_INFINITY);
        const maxTrackY = this.track.reduce((max, section) => Math.max(max, section.position.y + section.halfExtents.y), Number.NEGATIVE_INFINITY);

        const minX = Math.min(minTrackX); //, state.wall.position.x - state.wall.halfExtents.x, state.car.ToPosition.x - CAR_WIDTH_WORLD_UNITS);
        const maxX = Math.max(maxTrackX); //, state.wall.position.x + state.wall.halfExtents.x, state.car.ToPosition.x + CAR_WIDTH_WORLD_UNITS);
        const minY = Math.min(minTrackY); //, state.wall.position.y - state.wall.halfExtents.y, state.car.ToPosition.y - CAR_WIDTH_WORLD_UNITS);
        const maxY = Math.max(maxTrackY); //, state.wall.position.y + state.wall.halfExtents.y, state.car.ToPosition.y + CAR_WIDTH_WORLD_UNITS);

        const gridWidth = Math.max(maxX - minX, GRID_CELL_SIZE * 4);
        const gridHeight = Math.max(maxY - minY, GRID_CELL_SIZE * 4);
        this.spatialGridOrigin = new Vector((minX + maxX) / 2, (minY + maxY) / 2, 0);


        // const estimatedObjects = state.track.length + state.signs.length + state.signBoundaries.length + state.topWalls.length + state.bottomWalls.length + 2;
        const estimatedObjects = this.signBoundaries.length + this.topWalls.length + this.bottomWalls.length + 2;
        this.spatialGrid = new Grid<DynamicsGridObject>(
            gridWidth,
            gridHeight,
            GRID_CELL_SIZE,
            estimatedObjects,
            this.spatialGridOrigin);
        // this.spatialGridOrigin = new Vector(0, 0, 0);
        // this.spatialGridOrigin = Vector.Scale(new Vector(
        //     + (this.spatialGrid.width / 2),
        //     + (this.spatialGrid.height / 2),
        //     0
        // ), -1);

        // Initialize timing system
        //timeStep = new TimeStep(FIXED_TIMESTEP_MS);  // 33ms = ~30fps fixed timestep
        //fpsCounter = new Frequency();
        //logicCounter = new Frequency();
    }
}
//     this.app = app;
//     this.stateManager = stateManager;
//     this.container = new PIXI.Container();
// }

// onEnter(): void {
//     // Create title screen visuals
//     this.titleText = new PIXI.Text({
//         text: 'DRIVER',
//         style: {
//             fontFamily: 'Arial',
//             fontSize: 72,
//             fill: 0xffffff,
//             fontWeight: 'bold'
//         }
//     });
//     this.titleText.anchor.set(0.5);
//     this.titleText.x = this.app.screen.width / 2;
//     this.titleText.y = this.app.screen.height / 3;

//     this.subtitleText = new PIXI.Text({
//         text: 'Press any key or tap to start',
//         style: {
//             fontFamily: 'Arial',
//             fontSize: 24,
//             fill: 0xcccccc
//         }
//     });
//     this.subtitleText.anchor.set(0.5);
//     this.subtitleText.x = this.app.screen.width / 2;
//     this.subtitleText.y = this.app.screen.height / 2;

//     this.container.addChild(this.titleText);
//     this.container.addChild(this.subtitleText);
//     this.app.stage.addChild(this.container);

//     console.log('TitleState: Entered');
// }

//type GameState = {
//};

//function createGameState(): GameState {
//    return new GameState();
//}


//                                  /\
//                                  |
//   0          ------------   -----------
//   0.01            |
//   0.02            V
//   0.03              
//   0.04
//

// KeyboardEvent.code constants
const KEY_LEFT = 'ArrowLeft';
const KEY_UP = 'ArrowUp';
const KEY_RIGHT = 'ArrowRight';
const KEY_DOWN = 'ArrowDown';
const KEY_S = 'KeyS';
const KEY_D = 'KeyD';  // Toggle collision geometry
const KEY_I = 'KeyI';  // Toggle interpolation
const inputState = new InputState([KEY_LEFT, KEY_UP, KEY_RIGHT, KEY_DOWN]);

//     const collisionNudge = 0.0002;
const oneFoot = 1 / 5280
const CAR_WIDTH_WORLD_UNITS = 14.5 / 5280; // 0.015;
const GRID_CELL_SIZE_SCALE = 1.2;
const GRID_CELL_SIZE = CAR_WIDTH_WORLD_UNITS * GRID_CELL_SIZE_SCALE;
//const GRID_LINE_WIDTH = 0.00015;
const FIXED_TIMESTEP_MS = 33;
const SPARK_SPAWN_COOLDOWN_MS = 30;
const SPARK_MAX_LIFETIME_MS = 280;
const SPARKS_PER_SPAWN = 12;
const MAX_SPARK_COUNT = 240;
const SPARK_MIN_SPEED = 0.00004;
const SPARK_MAX_SPEED = 0.00014;
const BRAKE_FORCE_SCALE = 3.56;
const BRAKE_DRAG_SCALE = 4.8;

type DynamicsGridObjectType =
    | 'car'
    | 'wall'
    | 'signBoundary';

type DynamicsGridObject = GridObject<DynamicsGridObjectType>;

// 30mph = 30/60/60/1000 miles per milliscond
const unitForce: Vector = Vector.scale(new Vector(30 / 60 / 60 / 1000, 0, 0), (1 / 33) / 40);
// var CurrentForce = new Vector(0, 0, 0);  // Unused

function determineContactPosition(objectPosition: Vector, objectHalfExtents: Vector, planeNormal: Vector): Vector {
    // Assumes planeNormal is already normalized (unit vector)
    const contactRadius = Math.abs(planeNormal.x) * objectHalfExtents.x + Math.abs(planeNormal.y) * objectHalfExtents.y;
    return Vector.subtract(objectPosition, Vector.scale(planeNormal, contactRadius));
}

function spawnCollisionSparks(state: GameState, carPosition: Vector, carHalfExtents: Vector, planeNormal: Vector, incomingVelocity: Vector): void {
    if (state.elapsedMs - state.lastSparkSpawnMs < SPARK_SPAWN_COOLDOWN_MS) {
        return;
    }
    state.lastSparkSpawnMs = state.elapsedMs;

    const contactPosition = determineContactPosition(carPosition, carHalfExtents, planeNormal);

    // Extract only the velocity component parallel to the plane surface (sliding/scraping motion)
    const tangentialVelocity = Vector.projectOntoPlane(incomingVelocity, planeNormal);
    const tangentialSpeed = Vector.magnitude(tangentialVelocity);
    if (tangentialSpeed <= 1e-12) {
        return;
    }

    const trailDirection = Vector.scale(tangentialVelocity, -1 / tangentialSpeed);

    const newSparks = SparkParticle.spawnFromCollision(
        contactPosition,
        trailDirection,
        planeNormal,
        SPARKS_PER_SPAWN,
        SPARK_MIN_SPEED,
        SPARK_MAX_SPEED,
        SPARK_MAX_LIFETIME_MS
    );
    state.sparks.push(...newSparks);

    if (state.sparks.length > MAX_SPARK_COUNT) {
        state.sparks.splice(0, state.sparks.length - MAX_SPARK_COUNT);
    }
}

function createTouchHints(): PIXI.Container {
    const container = new PIXI.Container();

    const hintGraphics = new PIXI.Graphics();
    const textStyle = new PIXI.TextStyle({
        fill: 0xffffff,
        fontSize: 18,
        fontFamily: 'Arial',
        fontWeight: 'bold',
        stroke: { color: 0x000000, width: 3 }
    });

    // Function to draw a zone hint
    const drawZone = (x: number, y: number, width: number, height: number, color: number, labelText: string) => {
        // Semi-transparent background
        hintGraphics.rect(x, y, width, height);
        hintGraphics.fill({ color, alpha: 0.2 });

        // Border
        hintGraphics.rect(x, y, width, height);
        hintGraphics.stroke({ width: 2, color, alpha: 0.4 });

        // Label
        const label = new PIXI.Text({ text: labelText, style: textStyle });
        label.anchor.set(0.5);
        label.position.set(x + width / 2, y + height / 2);
        container.addChild(label);
    };

    const screenWidth = app.screen.width;
    const screenHeight = app.screen.height;
    const halfWidth = screenWidth / 2;
    const halfHeight = screenHeight / 2;

    // Left half = BRAKE
    drawZone(0, 0, halfWidth, screenHeight, 0xff4444, 'BRAKE');

    // Right half split into top (steer left) and bottom (steer right)
    drawZone(halfWidth, 0, halfWidth, halfHeight, 0x44ff44, 'STEER LEFT');
    drawZone(halfWidth, halfHeight, halfWidth, halfHeight, 0x4444ff, 'STEER RIGHT');

    container.addChild(hintGraphics);
    return container;
}

function getSignScale(): number {
    // Make sign scale based on window height - approximately 12% of screen height
    // Assuming sign texture is roughly 100px, this gives us nice visibility
    return (app.screen.height * 0.12) / 100;
}

function getSignPosition(): { x: number, y: number } {
    const scale = getSignScale();
    const padding = 30;
    // Get actual sign dimensions based on texture size (assuming ~100px square texture)
    const signHalfSize = 50 * scale; // Half of texture size * scale
    return {
        x: app.screen.width - padding - signHalfSize,
        y: padding + signHalfSize
    };
}

function getWarningPosition(): { x: number, y: number } {
    const signPos = getSignPosition();
    const scale = getSignScale();
    const signHalfSize = 50 * scale;
    // Position warning on the left side of the sign, overlapping it
    return {
        x: signPos.x - signHalfSize * 1.8,
        y: signPos.y
    };
}

function getWarningFontSize(): number {
    const scale = getSignScale();
    // Base font size is 48, scale it proportionally with sign but smaller
    return 48 * scale * 5; // Multiply by 5 to keep it smaller than the sign
}

function handleTouchInput(x: number, y: number, isPress: boolean) {
    const screenWidth = app.screen.width;
    const screenHeight = app.screen.height;
    const halfWidth = screenWidth / 2;
    const halfHeight = screenHeight / 2;

    // Mark as interacted (start fade timer if first interaction)
    if (isPress && !touchHints.hasInteracted) {
        touchHints.hasInteracted = true;
        touchHints.fadeStartTime = Date.now();
    }

    // Left half = BRAKE (KEY_LEFT)
    if (x < halfWidth) {
        if (isPress) {
            inputState.press(KEY_LEFT);
        } else {
            inputState.release(KEY_LEFT);
        }
    }

    // Right half = acceleration zone (not braking)
    if (x >= halfWidth) {
        if (!isPress) {
            inputState.release(KEY_LEFT);
        }

        // Top half = STEER LEFT (KEY_UP)
        if (y < halfHeight) {
            if (isPress) {
                inputState.press(KEY_UP);
            } else {
                inputState.release(KEY_UP);
            }
        }
        // Bottom half = STEER RIGHT (KEY_DOWN)
        else {
            if (isPress) {
                inputState.press(KEY_DOWN);
            } else {
                inputState.release(KEY_DOWN);
            }
        }
    } else {
        // If in brake zone, release steering
        if (!isPress) {
            inputState.release(KEY_UP);
            inputState.release(KEY_DOWN);
        }
    }
}

async function preloadAssets(assetList: string[]) {
    for (const asset of assetList) {
        await PIXI.Assets.load(asset);
    }
    console.log('All assets loaded');
}

export async function start() {
    console.log('Starting game...');
    
    // Check URL parameters for debug mode
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('debug')) {
        debugMode.showCollisionGeometry = true;
        console.log('Debug mode enabled via URL parameter');
    }
    
    // Start Tracking Keystokes
    document.addEventListener('keydown', (evt: KeyboardEvent) => {

        switch (evt.code) {
            case KEY_LEFT:
                inputState.press(KEY_LEFT);
                break;
            case KEY_UP:
                inputState.press(KEY_UP);
                break;
            case KEY_RIGHT:
                inputState.press(KEY_RIGHT);
                break;
            case KEY_DOWN:
                inputState.press(KEY_DOWN);
                break;
            case KEY_S:
                debugMode.steering = !debugMode.steering;
                console.log('Debug steering:', debugMode.steering);
                break;
            case KEY_D:
                debugMode.showCollisionGeometry = !debugMode.showCollisionGeometry;
                console.log('Debug geometry:', debugMode.showCollisionGeometry);
                break;
            case KEY_I:
                debugMode.disableInterpolation = !debugMode.disableInterpolation;
                console.log('Interpolation disabled:', debugMode.disableInterpolation);
                break;
        }

    });

    document.addEventListener('keyup', (evt: KeyboardEvent) => {
        switch (evt.code) {
            case KEY_LEFT:
                inputState.release(KEY_LEFT);
                break;
            case KEY_UP:
                inputState.release(KEY_UP);
                break;
            case KEY_RIGHT:
                inputState.release(KEY_RIGHT);
                break;
            case KEY_DOWN:
                inputState.release(KEY_DOWN);
                break;
        }
    });

    // Initialize PixiJS
    app = new PIXI.Application();
    await app.init({
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundColor: 0x000000,
        antialias: true
    });

    window.addEventListener('resize', () => {
        app.renderer.resize(window.innerWidth, window.innerHeight);

        // Update speed limit sign scale and position
        if (hudLimitSign) {
            const scale = getSignScale();
            hudLimitSign.scale.set(scale);
            const pos = getSignPosition();
            hudLimitSign.position.set(pos.x, pos.y);
        }

        // Update speeding warning position and scale
        if (hudSpeedingWarning) {
            const warningPos = getWarningPosition();
            hudSpeedingWarning.position.set(warningPos.x, warningPos.y);
            hudSpeedingWarning.style.fontSize = getWarningFontSize();
            hudSpeedingWarning.style.stroke = { color: 0xffff00, width: getSignScale() * 30 };
        }

        // Recreate touch hints on resize
        if (touchHints.container) {
            hudContainer.removeChild(touchHints.container);
            touchHints.container.destroy();
            touchHints.container = createTouchHints();
            touchHints.container.alpha = touchHints.opacity;
            hudContainer.addChild(touchHints.container);
            // Move to back so HUD text appears on top
            hudContainer.setChildIndex(touchHints.container, 0);
        }
    });

    // Append PixiJS canvas to container
    const container = document.getElementById("game-container");
    if (!container) throw new Error("Game container not found");
    container.appendChild(app.canvas);
    (app.canvas as HTMLCanvasElement).id = "myCanvas";

    // Preload all game assets

    // Create world container for camera transforms
    worldContainer = new PIXI.Container();
    app.stage.addChild(worldContainer);

    // Create HUD container (screen-space, unaffected by world camera transforms)
    hudContainer = new PIXI.Container();
    app.stage.addChild(hudContainer);

    await preloadAssets([
        '/images/signs/car.png',
        '/images/signs/wall.png',
        '/images/signs/track.png',
        '/images/signs/30.png',
        '/images/signs/50.png',
        '/images/signs/limit.png',
        '/images/signs/stop.png'
    ]);

    hudText = new PIXI.Text({
        text: '',
        style: {
            fill: 0xffffff,
            fontSize: 14,
            fontFamily: 'Arial'
        }
    });

    hudText.position.set(8, 8);
    hudContainer.addChild(hudText);

    // Create speed limit sign display
    const limitTexture = PIXI.Assets.get('/images/signs/30.png');
    hudLimitSign = PIXI.Sprite.from(limitTexture);
    hudLimitSign.anchor.set(0.5);
    const initialScale = getSignScale();
    hudLimitSign.scale.set(initialScale);
    const initialPos = getSignPosition();
    hudLimitSign.position.set(initialPos.x, initialPos.y);
    hudContainer.addChild(hudLimitSign);

    // Create speeding warning indicator
    hudSpeedingWarning = new PIXI.Text({
        text: '!',
        style: {
            fill: 0xff0000,
            fontSize: getWarningFontSize(),
            fontFamily: 'Arial',
            fontWeight: 'bold',
            stroke: { color: 0xffff00, width: getSignScale() * 30 }
        }
    });
    hudSpeedingWarning.anchor.set(0.5);
    const initialWarningPos = getWarningPosition();
    hudSpeedingWarning.position.set(initialWarningPos.x, initialWarningPos.y);
    hudSpeedingWarning.visible = false;
    hudContainer.addChild(hudSpeedingWarning);

    // Create touch control hints
    touchHints.container = createTouchHints();
    touchHints.container.alpha = touchHints.opacity;
    touchHints.fadeStartTime = Date.now();
    hudContainer.addChild(touchHints.container);

    // Add pointer/touch event handlers
    app.canvas.addEventListener('pointerdown', (evt: PointerEvent) => {
        evt.preventDefault();
        const rect = (app.canvas as HTMLCanvasElement).getBoundingClientRect();
        const x = evt.clientX - rect.left;
        const y = evt.clientY - rect.top;
        handleTouchInput(x, y, true);
    });

    app.canvas.addEventListener('pointerup', (evt: PointerEvent) => {
        evt.preventDefault();
        // Release all inputs when pointer is released
        inputState.release(KEY_LEFT);
        inputState.release(KEY_UP);
        inputState.release(KEY_DOWN);
    });

    app.canvas.addEventListener('pointermove', (evt: PointerEvent) => {
        // Update touch zone if pointer is down (dragging)
        if (evt.buttons > 0) {
            evt.preventDefault();
            const rect = (app.canvas as HTMLCanvasElement).getBoundingClientRect();
            const x = evt.clientX - rect.left;
            const y = evt.clientY - rect.top;

            // Release all first, then reapply based on new position
            inputState.release(KEY_LEFT);
            inputState.release(KEY_UP);
            inputState.release(KEY_DOWN);

            handleTouchInput(x, y, true);
        }
    });

    // Prevent context menu on long press
    app.canvas.addEventListener('contextmenu', (evt: Event) => {
        evt.preventDefault();
    });

    // Create debug graphics layer
    debugGraphics = new PIXI.Graphics();
    effectsGraphics = new PIXI.Graphics();


    const state = new GameState();
    await state.init();

    state.timeStep.start();

    worldContainer.addChild(effectsGraphics);
    worldContainer.addChild(debugGraphics);
    app.ticker.add(() => {
        state.run();
    });

}
